<script lang="ts">
	import FirstLevelNode from './_FirstLevelNode.svelte';
	import LanguageDropdown from './_LanguageDropdown.svelte';
	import LoginButton from './_LoginButton.svelte';
	import WorldsDropdown from './_WorldsDropdown.svelte';
	import letteringSvg from './svg/lettering.svg';
	import logoSvg from '$lib/logo.svg';
	import type {
		CmsLanguageRecord,
		CmsMainNavNodeRecord,
		CmsMainNavRecord,
		CmsWorldRecord
	} from '$lib/data/types';
	import { getAccountNavNodes } from './shared';
	import { isMobile } from '$lib/stores';
	import { locale, _ } from 'svelte-i18n';
	import { page } from '$app/stores';

	export let worlds: CmsWorldRecord[];
	export let languages: CmsLanguageRecord[];
	export let mainNav: CmsMainNavRecord;
	export let headerNotificationMessage: string | null | undefined;

	const nodes = mainNav.nodes.map((ref) => ref.main_nav_node_id);

	let hoveredNode: null | CmsMainNavNodeRecord = null;
	let clickedNode: null | CmsMainNavNodeRecord = null;
	let showMenu = false;

	async function toggleMenu() {
		showMenu = !showMenu;
		document.body.classList.toggle('navbar--opened', showMenu);

		if (!showMenu)
			// wait for CSS transition, then de-select active node to reset to root level
			setTimeout(() => (clickedNode = null), 250);
	}

	let isForceClosing = false;
	async function forceCloseMenu() {
		isForceClosing = true;
		showMenu = false;
		hoveredNode = null;
		document.body.classList.remove('navbar--opened');

		// wait for CSS transition, then de-select active node to reset to root level
		setTimeout(() => {
			clickedNode = null;
			isForceClosing = false;
		}, 250);
	}

	let collapsed = false;
	let scrollY = 0;
	let lastScrollY = 0;

	// navbar collapse on scroll
	$: {
		const delta = scrollY - lastScrollY;

		if (collapsed) {
			if (delta < 0) {
				collapsed = false;
			}
			lastScrollY = scrollY;
		} else {
			if (delta > 250) {
				collapsed = true;
				lastScrollY = scrollY;
			} else if (delta < 0) {
				lastScrollY = scrollY;
			}
		}
	}
</script>

<svelte:window bind:scrollY />

<header class:show-header-hint={headerNotificationMessage}>
	<nav class="navbar navbar--fixed" class:collapsed={$isMobile && collapsed}>
		<div class="container-fluid px-0">
			{#if headerNotificationMessage}
				<div class="navbar__hint">
					<p>{@html headerNotificationMessage}</p>
				</div>
			{/if}

			<div class="navbar__wrapper">
				<a
					href="/{$locale}/"
					class="navbar__brand"
					aria-label={$_('components.navbar.brandLink.label')}
				>
					<img
						class="logo"
						src={logoSvg}
						width="100%"
						height="100%"
						alt={$_('page.brand.shortName')}
					/>
					<img
						class="lettering d-xl-none"
						src={letteringSvg}
						width="60.674"
						height="32.8"
						alt="Ticket Shop"
					/>
				</a>

				<div class="navbar__collapse nav-drill">
					<ul class="navbar-nav navbar-nav--search">
						<!-- <SearchBar /> -->
						<LanguageDropdown {languages} />
					</ul>

					<WorldsDropdown {worlds} />

					<ul class="nav-items nav-level-1">
						{#if nodes}
							{@const nodesWithAccount = $page.data.session
								? [...nodes, ...getAccountNavNodes($page.data.vivenuExchangeToken)]
								: nodes}
							{#each nodesWithAccount as topNode}
								<FirstLevelNode
									node={topNode}
									{worlds}
									{isForceClosing}
									bind:hoveredNode
									bind:clickedNode
									on:close={forceCloseMenu}
								/>
							{/each}
						{/if}
					</ul>
				</div>

				<ul class="navbar-nav navbar-nav--btn-bar list-unstyled">
					<!-- <li class="nav-item"> -->
					<!-- 	<a href="#" role="button" data-testid="transactions-link"> -->
					<!-- 		<img -->
					<!-- 			class="cart-icon" -->
					<!-- 			src={$isMobile ? cartMobileSvg : cartSvg} -->
					<!-- 			width="150" -->
					<!-- 			height="150" -->
					<!-- 			alt="Einkaufswagen" -->
					<!-- 		/> -->
					<!-- 	</a> -->
					<!-- </li> -->

					<li class="nav-item">
						<LoginButton />
					</li>
				</ul>

				<button
					class="navbar__toggler"
					data-js="navbar-toggler"
					on:click|preventDefault={toggleMenu}
				>
					<span class="navbar__toggler-line" />
					<span class="navbar__toggler-line" />
					<span class="navbar__toggler-line" />

					<span class="navbar__toggler-text">Menü</span>
				</button>
			</div>
		</div>
	</nav>

	<div class="header-sidebar" class:collapsed={$isMobile && collapsed}>
		<a href="/{$locale}/" class="lettering" title={$_('page.titleShort')}>
			<img src={letteringSvg} width="60.674" height="32.8" alt={$_('page.titleShort')} />
		</a>
	</div>
</header>

<style>
	nav {
		transition: top 0.25s ease-in-out;
	}

	nav.collapsed {
		top: -7rem;
	}

	.header-sidebar {
		transition: top 0.25s linear;
	}

	.header-sidebar.collapsed {
		padding-top: 5em;
		top: 0;
	}

	.navbar__hint p {
		color: #040404;
		font-family: 'Schalke04Text-Bold', sans-serif;
	}
</style>
